import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { financeAPI } from '../lib/api';
import { FinanceSummary } from '../types';

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const response = await financeAPI.getRecords({ limit: 5 });
      if (response.success && response.data) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to load summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Section */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-4 sm:px-6 sm:py-5">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Welcome back, {user?.name}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Here's an overview of your financial dashboard.
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
          {/* Total Income */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" aria-hidden="true" />
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Income
                    </dt>
                    <dd className="text-lg sm:text-xl font-medium text-gray-900 truncate">
                      {formatCurrency(summary.income.total)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Expenses */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingDown className="h-6 w-6 sm:h-8 sm:w-8 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Expenses
                    </dt>
                    <dd className="text-lg sm:text-xl font-medium text-gray-900 truncate">
                      {formatCurrency(summary.expense.total)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Net Balance */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" aria-hidden="true" />
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Net Balance
                    </dt>
                    <dd className={`text-lg sm:text-xl font-medium truncate ${
                      summary.balance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(summary.balance)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Transactions */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-400" aria-hidden="true" />
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Transactions
                    </dt>
                    <dd className="text-lg sm:text-xl font-medium text-gray-900">
                      {summary.income.count + summary.expense.count}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-4 sm:px-6 sm:py-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Quick Actions
          </h3>
          <div className="mt-4 sm:mt-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <a
                href="/finance"
                className="relative group bg-white p-4 sm:p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg border border-gray-200 hover:shadow-md transition-shadow touch-manipulation"
              >
                <div>
                  <span className="rounded-lg inline-flex p-2 sm:p-3 bg-green-50 text-green-700 ring-4 ring-white">
                    <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-6 sm:mt-8">
                  <h3 className="text-base sm:text-lg font-medium">
                    <span className="absolute inset-0" aria-hidden="true" />
                    Add Income
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Record a new income transaction
                  </p>
                </div>
              </a>

              <a
                href="/finance"
                className="relative group bg-white p-4 sm:p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg border border-gray-200 hover:shadow-md transition-shadow touch-manipulation"
              >
                <div>
                  <span className="rounded-lg inline-flex p-2 sm:p-3 bg-red-50 text-red-700 ring-4 ring-white">
                    <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-6 sm:mt-8">
                  <h3 className="text-base sm:text-lg font-medium">
                    <span className="absolute inset-0" aria-hidden="true" />
                    Add Expense
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Record a new expense transaction
                  </p>
                </div>
              </a>

              <a
                href="/finance"
                className="relative group bg-white p-4 sm:p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg border border-gray-200 hover:shadow-md transition-shadow touch-manipulation sm:col-span-2 lg:col-span-1"
              >
                <div>
                  <span className="rounded-lg inline-flex p-2 sm:p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                    <Activity className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-6 sm:mt-8">
                  <h3 className="text-base sm:text-lg font-medium">
                    <span className="absolute inset-0" aria-hidden="true" />
                    View Reports
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    View detailed financial reports
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 